<div class="dash-shell uj">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >User journey</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">How an application moves through Flow</h1>
        <p class="page-head__subtitle">
          Whose move it is at each stage, what is asked for and how, from the first enquiry to the
          last repayment.
        </p>
      </div>
    </div>
  </header>

  <section class="uj-map" aria-labelledby="uj-map-title">
    <h2 id="uj-map-title" class="cdk-visually-hidden">The six stages</h2>
    <ul class="uj-key" aria-label="Who has the next move">
      <li data-ball="client">The business</li>
      <li data-ball="system">Flow</li>
      <li data-ball="capx">CapX</li>
      <li data-ball="both">Back and forth</li>
    </ul>

    <ol class="uj-rail">
      @for (stage of stages; track stage.key; let i = $index) {
        <li class="uj-rail__item">
          <button
            type="button"
            class="uj-stop"
            [attr.data-ball]="stage.ball"
            [attr.data-testid]="'uj-stage-' + stage.key"
            [class.uj-stop--on]="stage.key === selectedKey()"
            [attr.aria-pressed]="stage.key === selectedKey()"
            (click)="selectedKey.set(stage.key)"
          >
            <span class="uj-stop__num" aria-hidden="true">{{ i + 1 }}</span>
            <span class="uj-stop__title">{{ stage.title }}</span>
            <span class="uj-stop__ball">{{ ballLabel[stage.ball] }}</span>
          </button>
        </li>
      }
    </ol>

    @let stage = selected();
    <article
      class="uj-stage"
      [attr.data-ball]="stage.ball"
      data-testid="uj-stage-detail"
      aria-live="polite"
    >
      <header class="uj-stage__head">
        <span class="uj-stage__ball">{{ ballLabel[stage.ball] }}</span>
        <h3 class="uj-stage__title">{{ stage.title }}</h3>
        <p class="uj-stage__summary">{{ stage.summary }}</p>
      </header>
      <div class="uj-stage__body">
        <div>
          <h4 class="uj-label">What happens</h4>
          <ol class="uj-steps">
            @for (item of stage.happens; track item.text) {
              <li>
                {{ item.text }}
                @if (item.rule) {
                  <span class="uj-rule">{{ item.rule }}</span>
                }
              </li>
            }
          </ol>
        </div>
        <div class="uj-stage__side">
          <div class="uj-sees">
            <h4 class="uj-label">What the business sees</h4>
            <p>{{ stage.businessSees }}</p>
          </div>
          <div>
            <h4 class="uj-label">When it goes differently</h4>
            <ul class="uj-otherwise">
              @for (item of stage.otherwise; track item.text) {
                <li>
                  {{ item.text }}
                  @if (item.rule) {
                    <span class="uj-rule">{{ item.rule }}</span>
                  }
                </li>
              }
            </ul>
          </div>
        </div>
      </div>
    </article>
  </section>

  <section class="uj-section" aria-labelledby="uj-products-title">
    <h2 id="uj-products-title" class="uj-section__title">
      Kickstart Cash, Debtors Finance, or both
    </h2>
    <p class="uj-section__lead">
      One application starts every path. The business chooses no product, because it cannot be
      expected to know what CapX can offer.
    </p>
    <div class="uj-paths">
      @for (path of paths; track path.key) {
        <article class="uj-path" [attr.data-testid]="'uj-path-' + path.key">
          <span class="uj-path__tag">{{ path.tag }}</span>
          <h3 class="uj-path__title">{{ path.title }}</h3>
          <p class="uj-path__lead">{{ path.lead }}</p>
          <ul>
            @for (point of path.points; track point) {
              <li>{{ point }}</li>
            }
          </ul>
          <span class="uj-rule">{{ path.rule }}</span>
        </article>
      }
    </div>
  </section>

  <section class="uj-section" aria-labelledby="uj-talk-title">
    <h2 id="uj-talk-title" class="uj-section__title">The back and forth</h2>
    <p class="uj-section__lead">
      After submission Flow asks the business for nothing except what a rule makes the business's
      own act. Everything else is a person writing to a person.
    </p>
    <div class="uj-talk__heads" aria-hidden="true">
      <span>The business</span>
      <span>CapX</span>
    </div>
    <ol class="uj-talk">
      @for (exchange of exchanges; track exchange.title) {
        <li class="uj-msg" [attr.data-from]="exchange.from">
          <div class="uj-msg__bubble">
            <span class="cdk-visually-hidden">
              {{ exchange.from === 'business' ? 'The business:' : 'CapX:' }}
            </span>
            <span class="uj-channel" [attr.data-channel]="exchange.channel">
              {{ channelLabel[exchange.channel] }}
            </span>
            <strong>{{ exchange.title }}</strong>
            <p>{{ exchange.text }}</p>
            @if (exchange.rule) {
              <span class="uj-rule">{{ exchange.rule }}</span>
            }
          </div>
        </li>
      }
    </ol>
  </section>

  <section class="uj-section" aria-labelledby="uj-docs-title">
    <h2 id="uj-docs-title" class="uj-section__title">What is needed, and how it arrives</h2>
    <div class="uj-docs">
      <table class="uj-table">
        <thead>
          <tr>
            <th scope="col">Document</th>
            <th scope="col">From</th>
            <th scope="col">When</th>
            <th scope="col">How</th>
            <th scope="col">What it unlocks</th>
            <th scope="col">Rule</th>
          </tr>
        </thead>
        <tbody>
          @for (doc of documents; track doc.name) {
            <tr>
              <th scope="row">{{ doc.name }}</th>
              <td data-label="From">{{ doc.who }}</td>
              <td data-label="When">{{ doc.when }}</td>
              <td data-label="How">
                <span class="uj-channel" [attr.data-channel]="doc.channel">
                  {{ channelLabel[doc.channel] }}
                </span>
              </td>
              <td data-label="Unlocks">{{ doc.unlocks }}</td>
              <td data-label="Rule">
                <span class="uj-rule">{{ doc.rule }}</span>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="uj-section" aria-labelledby="uj-people-title">
    <h2 id="uj-people-title" class="uj-section__title">Who is involved</h2>
    <div class="uj-people">
      @for (person of people; track person.name) {
        <article class="uj-person" [attr.data-ball]="person.lane">
          <h3>{{ person.name }}</h3>
          <p>{{ person.wants }}</p>
          <p class="uj-person__limit">{{ person.mayNot }}</p>
        </article>
      }
    </div>
  </section>

  <section class="uj-section" aria-labelledby="uj-chart-title">
    <details class="uj-fold" data-testid="uj-chart">
      <summary>
        <span>
          <span id="uj-chart-title" class="uj-fold__title">Every step, with its rule</span>
          <span class="uj-fold__sub"
            >The same chart Ops sees on a file, with no file behind it.</span
          >
        </span>
        <app-icon name="chevron-down" [size]="18" />
      </summary>
      @if (journey(); as chart) {
        <app-pre-application-journey [journey]="chart" [showLegend]="false" />
      } @else if (chartError()) {
        <div class="uj-fold__state" role="alert">
          <p>The chart could not be loaded.</p>
          <button mat-stroked-button type="button" (click)="loadChart()">Try again</button>
        </div>
      } @else {
        <div class="uj-fold__state">
          <mat-spinner diameter="28" aria-label="Loading the chart" />
        </div>
      }
    </details>
  </section>

  <aside class="uj-open" aria-labelledby="uj-open-title">
    <h2 id="uj-open-title">What the rulebook does not settle</h2>
    <ul>
      @for (item of notSettled; track item) {
        <li>{{ item }}</li>
      }
    </ul>
  </aside>
</div>
